/**
 * Toolbar + Pager — composants UI réutilisables pour les tables admin.
 */
import { Search, ArrowUp, ArrowDown, Archive, ArchiveRestore, ChevronLeft, ChevronRight } from "lucide-react";

export function AdminTableToolbar({ table, placeholder = "Rechercher…", filters = [], extraActions = null }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-neutral-100 bg-white p-3" data-testid="admin-table-toolbar">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <input
          value={table.q}
          onChange={(e) => { table.setQ(e.target.value); table.setPage(1); }}
          placeholder={placeholder}
          className="w-full rounded-xl border border-neutral-200 bg-white pl-9 pr-3 py-2 text-sm focus:border-wine-500 focus:outline-none"
          data-testid="admin-table-search"
        />
      </div>
      {filters.map((f) => (
        <select
          key={f.key}
          value={table.filters[f.key] || ""}
          onChange={(e) => table.setFilters({ [f.key]: e.target.value })}
          className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 focus:border-wine-500 focus:outline-none"
          data-testid={`admin-table-filter-${f.key}`}
        >
          <option value="">{f.label}</option>
          {(f.options || []).map((o) => (
            <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
              {typeof o === "string" ? o : o.label}
            </option>
          ))}
        </select>
      ))}
      <button
        onClick={() => { table.setArchived(!table.archived); table.setPage(1); }}
        className={`inline-flex items-center gap-1 rounded-xl border px-3 py-2 text-xs font-bold ${table.archived ? "border-amber-300 bg-amber-50 text-amber-800" : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"}`}
        data-testid="admin-table-archived-toggle"
        title={table.archived ? "Voir les actifs" : "Voir les archivés"}
      >
        {table.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
        {table.archived ? "Archivés" : "Voir archivés"}
      </button>
      {extraActions}
    </div>
  );
}

export function AdminTablePager({ table }) {
  if (!table.pages || table.pages <= 1) {
    return <div className="px-4 py-3 text-xs text-neutral-500" data-testid="admin-table-pager-summary">{table.total} résultat{table.total > 1 ? "s" : ""}</div>;
  }
  const win = 2;
  const pageNums = [];
  for (let i = 1; i <= table.pages; i++) {
    if (i === 1 || i === table.pages || (i >= table.page - win && i <= table.page + win)) {
      pageNums.push(i);
    } else if (pageNums[pageNums.length - 1] !== "…") {
      pageNums.push("…");
    }
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-100 bg-neutral-50/40 px-4 py-3" data-testid="admin-table-pager">
      <div className="text-xs text-neutral-600">
        <strong>{table.total}</strong> résultat{table.total > 1 ? "s" : ""} — page <strong>{table.page}</strong> / {table.pages}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => table.setPage(Math.max(1, table.page - 1))}
          disabled={table.page <= 1}
          className="grid h-8 w-8 place-items-center rounded-lg border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 disabled:opacity-30"
          data-testid="admin-table-prev"
        ><ChevronLeft className="h-4 w-4" /></button>
        {pageNums.map((p, i) => (
          p === "…" ? (
            <span key={`e${i}`} className="px-2 text-xs text-neutral-400">…</span>
          ) : (
            <button
              key={p}
              onClick={() => table.setPage(p)}
              className={`h-8 min-w-[2rem] rounded-lg border px-2 text-xs font-bold ${p === table.page ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"}`}
              data-testid={`admin-table-page-${p}`}
            >{p}</button>
          )
        ))}
        <button
          onClick={() => table.setPage(Math.min(table.pages, table.page + 1))}
          disabled={table.page >= table.pages}
          className="grid h-8 w-8 place-items-center rounded-lg border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 disabled:opacity-30"
          data-testid="admin-table-next"
        ><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function SortHeader({ table, col, label, className = "" }) {
  const active = table.sort === col;
  return (
    <button
      onClick={() => table.toggleSort(col)}
      className={`inline-flex items-center gap-1 hover:text-wine-700 ${active ? "text-wine-700" : ""} ${className}`}
      data-testid={`admin-sort-${col}`}
    >
      {label}
      {active && (table.order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
    </button>
  );
}
